import { CornerDownRightIcon, UserIcon, UsersIcon } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, type UseFormTrigger } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FormCheckboxFieldDS } from 'components/reusable/form/FormFields'
import {
  Banner,
  Button,
  ButtonGroup,
  CheckboxGroup,
  TextButton,
  TextLink,
  useBreakpoint,
} from 'slab'
import { formatSSN, snakeToSentenceCase } from 'utils'
import {
  usePatchMemberOnboardingEnrollmentEventsEnrollmentEventIdAdditionalParticipantsBulkUpdate as useBulkUpdateAdditionalParticipants,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsFinalizeEnrollments as useFinalizeEnrollments,
} from 'utils/web_api'
import { MemberOnboardingMemberGender } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import {
  ArbitrationAgreementCard,
  ContractAgreementCard,
} from '../components/ConfirmEnrollmentAgreementCard'
import { ConfirmEnrollmentMemberCard } from '../components/ConfirmEnrollmentMemberCard'
import { ConfirmEnrollmentParticipantCard } from '../components/ConfirmEnrollmentParticipantCard'
import { CoveredParticipantsComparisonTable } from '../components/CoveredParticipantsComparisonTable/CoveredParticipantsComparisonTable'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { QLEButtonLayout } from '../components/QLEButtonLayout'
import { QleEarlySubmitBanner } from '../components/QleEarlySubmitBanner'
import { SelectedPlansComparisonTable } from '../components/SelectedPlansComparisonTable/SelectedPlansComparisonTable'
import { SignatureIcon } from '../components/SignatureIcon'
import { clearOnboardingStorage } from '../navigationUtils'
import { useOnboarding } from '../OnboardingContext'
import { type ConfirmEnrollmentFormData, type StepComponentProps } from '../types'
import { isEnrollmentEventRenewal, isNewQleFlow, isRenewalOrQleFlow } from '../utils'

type ParticipantFieldPrefix = 'member' | `dependent_${string}`
export type SSNField = `${ParticipantFieldPrefix}.ssn`
export const DUPLICATE_SSN_ERROR_MESSAGE = 'This SSN or ITIN was already entered for someone else'

type SSNFormData = {
  fieldId: SSNField;
  ssn: string;
}

/**
 * Helper function to check that this SSN does not match any other SSN in the form.
 * Also handles smart revalidation of other SSN fields to clear duplicate errors.
 *
 * @param currentSSN - The SSN to validate
 * @param currentFieldId - The field ID of the current SSN
 * @param formData - The form data to validate
 * @param trigger - The trigger function to revalidate other SSN fields
 * @param prevSSNValuesRef - Ref to track previous SSN values for infinite loop prevention
 * @returns A string error message if the SSN is not unique, or true if it is unique
 */
export const validateUniqueSSN = (
  currentSSN: string,
  currentFieldId: SSNField,
  formData: ConfirmEnrollmentFormData,
  trigger: UseFormTrigger<ConfirmEnrollmentFormData>,
  prevSSNValuesRef: React.MutableRefObject<Partial<Record<SSNField, string>>>
): string | boolean => {
  if (!currentSSN) return true // Allow empty values (required validation will handle this)

  // Normalize the SSN for comparison (remove formatting)
  const normalizedCurrentSSN = currentSSN.replace(/\D/g, '')
  if (normalizedCurrentSSN.length !== 9) return true // Let pattern validation handle format issues

  // Collect all SSNs from the form data
  const allSSNFormData: SSNFormData[] = []

  // Collect member SSN
  if (formData.member?.ssn) {
    const memberSSN = formData.member.ssn.replace(/\D/g, '')
    if (memberSSN.length === 9) {
      allSSNFormData.push({ fieldId: 'member.ssn', ssn: memberSSN })
    }
  }

  // Collect dependent SSNs
  Object.entries(formData).forEach(([key, value]) => {
    if (key.startsWith('dependent_') && value && typeof value !== 'boolean' && 'ssn' in value) {
      const dependentSSN = value.ssn.replace(/\D/g, '')
      if (dependentSSN.length === 9) {
        allSSNFormData.push({ fieldId: `${key}.ssn` as SSNField, ssn: dependentSSN })
      }
    }
  })

  // Check for duplicates (excluding the current field)
  const otherSSNFormData: SSNFormData[] = allSSNFormData.filter(item => item.fieldId !== currentFieldId)
  const duplicateSSNFormData: SSNFormData | undefined = otherSSNFormData.find(item => item.ssn === normalizedCurrentSSN)

  if (duplicateSSNFormData) {
    return DUPLICATE_SSN_ERROR_MESSAGE
  }

  // === No duplicates found ===
  // 1. If this SSN's previous value was causing duplicates, trigger validation on other SSN fields
  // (i.e. clear SSN duplicate errors from any affected fields)
  // 2. Update the previous value
  // 3. Return true so that the field is considered valid

  const previousValue = prevSSNValuesRef.current[currentFieldId] || ''
  const normalizedPrevious = previousValue.replace(/\D/g, '')

  // Only trigger if the value actually changed and was previously causing duplicates
  // Prevents validation infinite loop
  if (normalizedPrevious !== normalizedCurrentSSN && normalizedPrevious.length === 9) {
    const wasCreatingDuplicates = otherSSNFormData.some(({ ssn }) => ssn === normalizedPrevious)

    if (wasCreatingDuplicates) {
      otherSSNFormData.forEach(({ fieldId }) => {
        // Trigger validation on other SSN fields
        trigger(fieldId)
      })
    }
  }

  // Update the previous value
  prevSSNValuesRef.current[currentFieldId] = currentSSN

  return true
}

const getNYOrganDonorOptInValue = (nyOrganDonorOptIn: string | undefined) => {
  if (nyOrganDonorOptIn === 'yes') {
    return 'true'
  } else if (nyOrganDonorOptIn === 'no') {
    return 'false'
  }
  return undefined
}

// Helper function to prepare member data for submission
export const prepareMemberData = (formData: ConfirmEnrollmentFormData) => {
  const memberData = {
    gender: formData.member.gender as MemberOnboardingMemberGender,
    occupation: formData.member.occupation,
    ssn: formData.member.ssn.replace(/\D/g, ''),
    is_us_citizen: formData.member.is_us_citizen || false,
    sms_ai_opt_in: formData.member.sms_opt_in,
    sms_account_notification_opt_in: formData.member.sms_opt_in,
    ny_organ_donor_opt_in: getNYOrganDonorOptInValue(formData.member.ny_organ_donor_opt_in),
  }

  const participantsData = Object.entries(formData)
    .filter(([key]) => key.startsWith('dependent_'))
    .map(([key, value]) => {
      const participantId = key.replace('dependent_', '')
      if (
        value &&
        typeof value !== 'boolean' &&
        'gender' in value &&
        'ssn' in value
      ) {
        return {
          id: participantId,
          gender: value.gender as MemberOnboardingMemberGender,
          ssn: value.ssn.replace(/\D/g, ''),
        }
      }
      return null
    })

  return { memberData, participantsData }
}

// Helper function to handle member validation errors
export const handleMemberValidationErrors = (
  error: {
    status: number;
    fieldErrors?: {
      member: Record<string, string[]>;
      arbitration_signature?: string[];
      contract_signature?: string[];
    };
    errors?: string[];
  },
  setError: (name: any, error: { type: string; message: string }) => void
): GrapeAPIErrorType[] => {
  const errors: GrapeAPIErrorType[] = []
  if (error.status === 422) {
    if (error.fieldErrors?.member) {
      Object.entries(error.fieldErrors.member).forEach(
        ([memberField, messages]) => {
          setError(`member.${memberField}` as any, {
            type: 'manual',
            message: messages.join(', '),
          })
        }
      )
    } else if (error.errors) {
      error.errors.forEach((errorMsg) => {
        // Extract field name from error message (e.g., "member[ssn] must be exactly 9 digits" -> "ssn")
        const match = errorMsg.match(/member\[(\w+)\]/)
        if (match) {
          const field = match[1]
          // Extract just the error message part after the field path
          const message = errorMsg.replace(/^member\[\w+\]\s+/, '')
          setError(`member.${field}` as any, {
            type: 'manual',
            message,
          })
        }
      })
    }
    // Handle signature validation errors
    if (error.fieldErrors?.arbitration_signature) {
      setError('arbitration_agreement.self_signature' as any, {
        type: 'manual',
        message: error.fieldErrors.arbitration_signature.join(', '),
      })
    }
    if (error.fieldErrors?.contract_signature) {
      setError('contract_agreement.self_signature' as any, {
        type: 'manual',
        message: error.fieldErrors.contract_signature.join(', '),
      })
    }
  } else {
    errors.push(error as GrapeAPIErrorType)
  }
  return errors
}

// Helper function to handle participant validation errors
export const handleParticipantValidationErrors = (
  error: {
    status: number;
    fieldErrors?: {
      participants: Record<string, Record<string, string[]>>;
    };
    errors?: string[];
  },
  setError: (name: any, error: { type: string; message: string }) => void,
  participantsData: Array<{ id: string } | null>
): GrapeAPIErrorType[] => {
  const errors: GrapeAPIErrorType[] = []
  if (error.status === 422) {
    if (error.fieldErrors?.participants) {
      Object.entries(error.fieldErrors.participants).forEach(
        ([participantId, participantErrors]) => {
          Object.entries(participantErrors).forEach(([field, messages]) => {
            setError(`dependent_${participantId}.${field}` as any, {
              type: 'manual',
              message: messages.join(', '),
            })
          })
        }
      )
    } else if (error.errors) {
      error.errors.forEach((errorMsg) => {
        // Extract participant index and field from error message (e.g., "participants[0][ssn] must be exactly 9 digits" -> ["0", "ssn"])
        const match = errorMsg.match(/participants\[(\d+)\]\[(\w+)\]/)
        if (match) {
          const [, index, field] = match
          const participantId = participantsData[parseInt(index)]?.id
          if (participantId) {
            // Extract just the error message part after the field path
            const message = errorMsg.replace(
              /^participants\[\d+\]\[\w+\]\s+/,
              ''
            )
            setError(`dependent_${participantId}.${field}` as any, {
              type: 'manual',
              message,
            })
          }
        }
      })
    }
  } else {
    errors.push(error as GrapeAPIErrorType)
  }
  return errors
}

type PrefilledFieldWithDefaults = 'gender' | 'occupation'

export function ConfirmAndEnrollPage ({
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps) {
  const { assign } = useWindowLocation()

  const {
    onboardingData,
    enrollmentEventId,
    updateMemberInfo,
    prefillDummyData,
    planChangeSummary,
    newQleEnabled,
    qleMetadata,
  } = useOnboarding()
  const [submitError, setSubmitError] = useState<React.ReactNode | null>(null)
  const { breakpointIsAtLeast } = useBreakpoint()

  // Determine if this is a renewal or QLE flow
  const isRenewalOrQle = onboardingData
    ? isRenewalOrQleFlow(onboardingData, newQleEnabled)
    : false
  const isRenewal = isEnrollmentEventRenewal(onboardingData?.enrollment_event)
  const isQLEFlow = onboardingData ? isNewQleFlow(onboardingData, newQleEnabled) : false

  const earliestTimeToSubmit = qleMetadata?.earliest_time_to_submit ?? null

  const hasDependent =
    onboardingData?.additional_participants &&
    onboardingData.additional_participants.length > 0

  const restrictions = onboardingData?.restrictions
  const binaryGenderRestriction =
    restrictions?.binary_gender_required?.length &&
    restrictions.binary_gender_required.length > 0

  const needContractAgreement =
    onboardingData?.missing_fields?.enrollment?.includes('carrier_contract')
  const needArbitrationAgreement =
    onboardingData?.missing_fields?.enrollment?.includes('carrier_arbitration')
  const needUserPlatformAgreement =
    onboardingData?.missing_fields?.enrollment?.includes(
      'user_platform_agreement'
    )

  const genderOptions = useMemo(
    () =>
      Object.values(MemberOnboardingMemberGender)
        .filter((gender) => {
          if (binaryGenderRestriction) {
            return gender !== MemberOnboardingMemberGender.non_binary
          }
          return true
        })
        .map((gender) => ({
          value: gender,
          label: snakeToSentenceCase(gender),
        })),
    [binaryGenderRestriction]
  )

  const bulkUpdateAdditionalParticipants =
    useBulkUpdateAdditionalParticipants()
  const finalizeEnrollments = useFinalizeEnrollments()

  const isAnyMutationInProgress =
    updateMemberInfo.isPending ||
    bulkUpdateAdditionalParticipants.isPending ||
    finalizeEnrollments.isPending

  // only prefilling member info for this page
  const getDummyData = (field: PrefilledFieldWithDefaults) => {
    if (!prefillDummyData) {
      return ''
    }

    return {
      gender: 'male',
      occupation: 'doctor',
    }[field]
  }

  // Determine if member has missing fields
  // API returns enrollment-step missing fields in missing_fields.enrollment
  const memberHasMissingFields = useMemo(() => {
    const enrollmentFields = onboardingData?.missing_fields?.enrollment || []
    return enrollmentFields.some((field) => field.startsWith('member.'))
  }, [onboardingData?.missing_fields?.enrollment])

  // Determine which participants need updates (SSN or gender for renewal/QLE flow)
  // API returns participant SSN/gender in missing_fields.enrollment with format additional_participants.{id}.ssn
  const participantsNeedingUpdate = useMemo(() => {
    if (!onboardingData?.additional_participants) {
      return []
    }

    const enrollmentFields = onboardingData?.missing_fields?.enrollment || []

    return onboardingData.additional_participants.filter((participant) => {
      // Check if SSN is needed (API uses plural: additional_participants)
      const needsSSN = enrollmentFields.some(
        (field) => field === `additional_participants.${participant.id}.ssn`
      )

      // Check if gender needs to be updated (binary required but participant has non-binary)
      const needsBinaryGender =
        binaryGenderRestriction &&
        participant.gender === MemberOnboardingMemberGender.non_binary

      return needsSSN || needsBinaryGender
    })
  }, [
    onboardingData?.additional_participants,
    onboardingData?.missing_fields?.enrollment,
    binaryGenderRestriction,
  ])

  const getDefaultValues = () => {
    const member = onboardingData?.member

    const additionalParticipants = onboardingData?.additional_participants

    // For renewal/QLE: only include participants that need updates (SSN or gender)
    // For initial enrollment: include all participants (we collect SSN and gender at the end of the flow, which is in this page)
    const additionalDependentsValues = additionalParticipants?.reduce(
      (acc, participant) => {
        if (isRenewalOrQle) {
          // Only include if they need updates (API uses enrollment and plural: additional_participants)
          const needsSSN = onboardingData?.missing_fields?.enrollment?.some(
            (field) => field === `additional_participants.${participant.id}.ssn`
          )
          const needsBinaryGender =
            binaryGenderRestriction &&
            participant.gender === MemberOnboardingMemberGender.non_binary

          if (!needsSSN && !needsBinaryGender) return acc
        }

        return {
          ...acc,
          [`dependent_${participant.id}`]: {
            gender: participant.gender || '',
            ssn: '', // we do not get SSN by default, used only for PATCH
          },
        }
      },
      {}
    )

    const needNYOrganDonorOptIn =
      onboardingData?.missing_fields?.enrollment?.includes('ny_organ_donor_opt_in')
    const hasNYOrganDonorValue = member?.ny_opted_in_organ_donor != null
    let nyOrganDonorDefaultValue: string | undefined

    // to avoid a case of not submitting anything for organ donor, provide a default value of
    // "not yet", which causes no action to be taken by our carriers
    if (hasNYOrganDonorValue) {
      nyOrganDonorDefaultValue = (member as any).ny_opted_in_organ_donor ? 'yes' : 'no'
    } else if (needNYOrganDonorOptIn) {
      nyOrganDonorDefaultValue = 'no'
    }

    // For renewal/QLE: only populate member fields if they have missing fields
    // For initial enrollment: always populate
    const shouldPopulateMemberFields = !isRenewalOrQle || memberHasMissingFields

    const defaultValues = {
      member: {
        gender: shouldPopulateMemberFields
          ? (member?.gender || getDummyData('gender'))
          : '',
        occupation: shouldPopulateMemberFields
          ? (member?.occupation || getDummyData('occupation'))
          : '',
        is_us_citizen: shouldPopulateMemberFields
          ? (member?.is_us_citizen || null)
          : null,
        ssn: '', // we do not get SSN by default, used only for PATCH
        sms_opt_in:
          member?.sms_ai_opt_in && member?.sms_account_notification_opt_in
            ? true
            : null,
        ...(nyOrganDonorDefaultValue !== undefined ? { ny_organ_donor_opt_in: nyOrganDonorDefaultValue } : {}),
      },
      arbitration_agreement: needArbitrationAgreement
        ? {
            self_signature: '',
          }
        : undefined,
      contract_agreement: needContractAgreement
        ? {
            self_signature: '',
          }
        : undefined,
      user_platform_agreement: needUserPlatformAgreement ? false : undefined,
      ...additionalDependentsValues,
    }

    return defaultValues
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors: _clearErrors,
    trigger,
    control,
    formState: { errors, isValid },
  } = useForm<ConfirmEnrollmentFormData>({
    defaultValues: getDefaultValues(),
    mode: 'onTouched',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    const values = getDefaultValues()
    reset(values)
  }, [onboardingData])

  // Track previous SSN values for validateUniqueSSN
  const prevSSNValuesRef = useRef<Partial<Record<SSNField, string>>>({})

  // Create validation functions for SSN uniqueness
  const createUniqueSSNValidator = (fieldId: SSNField) => (value: string) => {
    const currentFormData = watch()
    return validateUniqueSSN(value, fieldId, currentFormData, trigger, prevSSNValuesRef)
  }

  const onSubmit = async (formData: ConfirmEnrollmentFormData) => {
    try {
      // Always prepare the full data first
      const prepared = prepareMemberData(formData)
      let memberData: any = prepared.memberData
      let participantsData = prepared.participantsData.filter(
        (p): p is { id: string; gender: MemberOnboardingMemberGender; ssn: string } =>
          p !== null
      )

      // For renewal/QLE: conditionally filter/transform the data
      if (isRenewalOrQle) {
        // Member data - only include all fields if member has missing fields
        if (!memberHasMissingFields) {
          // Only keep SMS opt-in fields
          memberData = {
            sms_ai_opt_in: memberData.sms_ai_opt_in,
            sms_account_notification_opt_in: memberData.sms_account_notification_opt_in,
          }
        }

        // Participant data - only include those that need updates
        const participantIdsNeedingUpdate = new Set(
          participantsNeedingUpdate.map((p) => p.id)
        )
        participantsData = participantsData.filter((p) =>
          participantIdsNeedingUpdate.has(p.id)
        )
      }

      // Run both mutations and handle their results independently
      const [memberResult, participantsResult] = await Promise.allSettled([
        // Update member info
        updateMemberInfo.mutateAsync({
          member: memberData,
          signatory: {
            arbitration_agreement:
              formData.arbitration_agreement?.self_signature,
            contract_agreement: formData.contract_agreement?.self_signature,
            user_platform_agreement:
              formData.user_platform_agreement === true ? true : undefined,
          },
        }),
        // Update additional participants (only if there are any)
        participantsData.length > 0
          ? bulkUpdateAdditionalParticipants.mutateAsync({
            enrollmentEventId,
            data: {
              participants: participantsData,
            },
          })
          : Promise.resolve(),
      ])

      // Check results of both mutations
      const errors: GrapeAPIErrorType[] = []

      if (memberResult.status === 'rejected') {
        const error = memberResult.reason as {
          status: number;
          fieldErrors?: { member: Record<string, string[]> };
          errors?: string[];
        }
        errors.push(...handleMemberValidationErrors(error, setError))
      }

      if (participantsResult.status === 'rejected') {
        const error = participantsResult.reason as {
          status: number;
          fieldErrors?: {
            participants: Record<string, Record<string, string[]>>;
          };
          errors?: string[];
        }
        errors.push(
          ...handleParticipantValidationErrors(
            error,
            setError,
            participantsData
          )
        )
      }

      // Only proceed with finalization if there are no errors and both mutations succeeded
      if (
        errors.length === 0 &&
        memberResult.status === 'fulfilled' &&
        (participantsData.length === 0 || participantsResult.status === 'fulfilled')
      ) {
        try {
          await finalizeEnrollments.mutateAsync({
            enrollmentEventId,
            data: {
              enrollment_or_selected_plan_ids:
                onboardingData?.enrollments?.map((e) => e.id) || [],
            },
          })
          if (onboardingData?.enrollment_event?.type === 'special_enrollment') {
            window.segment?.track('[EE] QLE completed', {
              enrollment_event_id: enrollmentEventId,
              qle_subtype: onboardingData?.enrollment_event?.subtype,
            })
          }
          clearOnboardingStorage(enrollmentEventId)
          assign('/member_onboarding/post_enrollment')
        } catch (error) {
          const errorMessage = (
            <OnboardingGenericErrorMessage error={error as GrapeAPIErrorType} />
          )
          setSubmitError(errorMessage)
        }
      }

      if (errors.length > 0) {
        const errorMessage = <OnboardingGenericErrorMessage errors={errors} />
        setSubmitError(errorMessage)
      }
    } catch (error) {
      const errorMessage = (
        <OnboardingGenericErrorMessage error={error as GrapeAPIErrorType} />
      )
      setSubmitError(errorMessage)
    }
  }

  const isMemberCardValid = () => {
    const memberValues = watch('member')
    return (
      (!errors.member || Object.keys(errors.member).length === 0) &&
      memberValues.gender !== '' &&
      memberValues.occupation !== '' &&
      memberValues.is_us_citizen !== null &&
      memberValues.ssn !== '' &&
      formatSSN(memberValues?.ssn || '').length === 11
    )
  }

  const isDependentCardValid = (participantId: string) => {
    const dependentKey = `dependent_${participantId}` as const
    const dependentValues = watch(dependentKey)

    return (
      (!errors[dependentKey] ||
        Object.keys(errors[dependentKey] || {}).length === 0) &&
      dependentValues?.gender !== '' &&
      dependentValues?.ssn !== '' &&
      formatSSN(dependentValues?.ssn || '').length === 11
    )
  }

  const selfName = (onboardingData?.member?.participant_name || '')

  const isAgreementValid = (type: 'arbitration' | 'contract') => {
    const agreementValues = watch(`${type}_agreement`)

    // Member signature is always required
    const memberSignatureValid =
      !errors[`${type}_agreement`]?.self_signature &&
      agreementValues?.self_signature !== '' &&
      agreementValues?.self_signature.trim().toLocaleLowerCase() === selfName.trim().toLocaleLowerCase()

    return memberSignatureValid
  }

  const generalMedicalEnrollment = onboardingData?.enrollments?.find((e) => {
    return e.covers_medical === true && e.payment_method === 'paid_by_thatch'
  })
  const carrierName = generalMedicalEnrollment?.carrier_name

  function renderButtons () {
    const declineButton = (
      <TextButton
        color={isRenewalOrQle ? undefined : 'purple'}
        onClick={() => {
          navigateToSpecificStep?.('opted_out')
        }}
        disabled={isAnyMutationInProgress}
      >
        {isRenewalOrQle ? 'Decline coverage' : 'Decline your coverage'}
      </TextButton>
    )

    const backButton = (
      <Button
        variant='secondary'
        size={isRenewalOrQle ? 'large' : undefined}
        onClick={() => {
          navigateToPreviousStep?.(onboardingData)
        }}
        disabled={isAnyMutationInProgress}
      >
        Back
      </Button>
    )

    const submitButton = (
      <Button
        rightIcon={CornerDownRightIcon}
        variant='primary'
        size={isRenewalOrQle ? 'large' : undefined}
        disabled={
          isAnyMutationInProgress ||
          !isValid ||
          (isRenewalOrQle && (onboardingData?.enrollments?.map((e) => e.id) || []).length < 1) ||
          shouldShowQleEarlySubmitBanner
        }
        type='submit'
      >
        {buttonText}
      </Button>
    )

    const isDesktop = breakpointIsAtLeast('xs')

    if (isQLEFlow) {
      return (
        <div className={isDesktop ? 'mt-16' : 'mt-10'}>
          <QLEButtonLayout
            backButton={backButton}
            rightButtons={[submitButton]}
            navigateToSpecificStep={navigateToSpecificStep}
          />
        </div>
      )
    }

    if (isRenewalOrQle) {
      if (breakpointIsAtLeast('xs')) {
        return (
          <div className='flex justify-between items-center'>
            {declineButton}
            <ButtonGroup size='large'>
              {backButton}
              {submitButton}
            </ButtonGroup>
          </div>
        )
      }

      return (
        <div className='flex flex-col gap-4'>
          <ButtonGroup size='large' layout='vertical'>
            {submitButton}
            {backButton}
          </ButtonGroup>
          {declineButton}
        </div>
      )
    }

    // Initial enrollment layout
    if (breakpointIsAtLeast('xs')) {
      return (
        <div className='flex justify-between items-center mt-6'>
          {declineButton}
          <ButtonGroup size='large'>
            {backButton}
            {submitButton}
          </ButtonGroup>
        </div>
      )
    }

    return (
      <div className='flex flex-col gap-4'>
        <ButtonGroup size='large' layout='vertical'>
          {submitButton}
          {backButton}
        </ButtonGroup>
        {declineButton}
      </div>
    )
  }

  // Page title and button text
  const pageTitle = isRenewal
    ? `Confirm your ${planChangeSummary?.next_plan_year?.plan_year} coverage`
    : 'Confirm and enroll'
  const buttonText = isRenewal ? 'Renew' : 'Enroll'

  // Determine which participants to show (all for initial, only those needing updates for renewal/QLE)
  const participantsToShow = isRenewalOrQle
    ? participantsNeedingUpdate
    : onboardingData?.additional_participants || []

  // Determine if we should show member form
  const shouldShowMemberForm = !isRenewalOrQle || memberHasMissingFields

  const shouldShowQleEarlySubmitBanner: boolean = !!(earliestTimeToSubmit && new Date(earliestTimeToSubmit) > new Date())

  return (
    <div
      data-testid={
        isRenewalOrQle ? 'RenewalConfirmAndEnrollPage' : 'ConfirmAndEnrollPage'
      }
      className='max-w-[44rem]'
    >
      <div className={isRenewalOrQle ? 'mb-10' : 'mb-6'}>
        <h1 className='heading-lg text-gray-800 mb-3'>
          {pageTitle}
        </h1>
        {!isRenewalOrQle && (
          <p className='body text-gray-500'>
            We need a few more details to enroll you
            {hasDependent ? ' and your dependents' : ''}.
          </p>
        )}
      </div>
      <form className='flex flex-col gap-y-6' onSubmit={handleSubmit(onSubmit)}>
        {/* Show comparison tables for renewal/QLE */}
        {isRenewalOrQle && onboardingData && planChangeSummary && (
          <>
            <CoveredParticipantsComparisonTable
              planChangeSummary={planChangeSummary}
              onboardingData={onboardingData}
            />
            <SelectedPlansComparisonTable
              planChangeSummary={planChangeSummary}
              onboardingData={onboardingData}
            />
          </>
        )}

        {/* Show member form conditionally */}
        {shouldShowMemberForm && (
          <div className='flex flex-col gap-y-4'>
            <h4 className='flex items-center gap-x-2 body-bold text-gray-800'>
              <UserIcon className='text-gray-500 font-normal' size={16} />
              Your details
            </h4>
            <ConfirmEnrollmentMemberCard
              control={control}
              register={register}
              errors={errors}
              isFormSubmitting={isAnyMutationInProgress}
              isEverythingValid={isMemberCardValid()}
              genderOptions={genderOptions}
              validateUniqueSSN={createUniqueSSNValidator('member.ssn')}
            />
          </div>
        )}

        {/* Show participant forms conditionally */}
        {participantsToShow.length > 0 && (
          <div className='flex flex-col gap-y-2'>
            <h4 className='flex items-center gap-x-2 body-bold text-gray-800 mb-2'>
              <UsersIcon className='text-gray-500 font-normal' size={16} />
              {participantsToShow.length > 1
                ? "Dependents' details"
                : "Dependent's details"}
            </h4>
            {participantsToShow.map((participant) => (
              <ConfirmEnrollmentParticipantCard
                key={participant.id}
                coveredParticipant={participant}
                register={register}
                errors={errors}
                isFormSubmitting={isAnyMutationInProgress}
                isEverythingValid={isDependentCardValid(participant.id)}
                genderOptions={genderOptions}
                validateUniqueSSN={createUniqueSSNValidator(`dependent_${participant.id}.ssn`)}
              />
            ))}
          </div>
        )}
        {(needContractAgreement || needArbitrationAgreement) && (
          <div className='flex flex-col gap-y-2'>
            <h4 className='flex items-center gap-x-2 body-bold text-gray-800 mb-2'>
              <SignatureIcon className='text-gray-500 font-normal' />
              Signatures
            </h4>
            {needArbitrationAgreement && (
              <ArbitrationAgreementCard
                register={register}
                errors={errors}
                isFormSubmitting={isAnyMutationInProgress}
                isEverythingValid={isAgreementValid('arbitration')}
                carrierName={carrierName}
              />
            )}
            {needContractAgreement && (
              <ContractAgreementCard
                register={register}
                errors={errors}
                isFormSubmitting={isAnyMutationInProgress}
                isEverythingValid={isAgreementValid('contract')}
              />
            )}
          </div>
        )}
        {needUserPlatformAgreement && (
          <div className='mt-4'>
            <FormCheckboxFieldDS<ConfirmEnrollmentFormData>
              fieldId='user_platform_agreement'
              fieldLabel={

                <>
                  I agree to the{' '}
                  <TextLink
                    href='https://thatch.com/legal/member-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Member Agreement
                  </TextLink>
                  ,{' '}
                  <TextLink
                    href='https://thatch.com/legal/financial-partners/consumer-deposit-account-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Consumer Deposit Account Agreement
                  </TextLink>
                  , and{' '}
                  <TextLink
                    href='https://thatch.com/legal/financial-partners/debit-cardholder-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Debit Cardholder Agreement
                  </TextLink>
                  .
                </>

              }
              error={errors.user_platform_agreement}
              disabled={isAnyMutationInProgress}
              register={register}
              validationRule={{
                ...(needUserPlatformAgreement
                  ? {
                      required: 'You must agree to these agreements to enroll.',
                    }
                  : {}),
              }}
            />
          </div>
        )}
        {/* Show Thatchbot opt-in checkbox conditionally */}
        {(() => {
          const showThatchbotOptInCheckbox =
            onboardingData?.member?.sms_ai_opt_in !== true ||
            onboardingData?.member?.sms_account_notification_opt_in !== true

          if (!showThatchbotOptInCheckbox) return null

          return (
            <div className='mt-4'>
              <CheckboxGroup label='Thatch is better with text messaging (recommended)'>
                <FormCheckboxFieldDS<ConfirmEnrollmentFormData>
                  fieldId='member.sms_opt_in'
                  fieldLabel='I consent to Thatch sending SMS notifications about my purchases.'
                  register={register}
                  disabled={isAnyMutationInProgress}
                />
                <p className='text-xs leading-[18px] font-normal text-gray-500 ml-7'>
                  Message & data rates may apply. Text STOP to opt out at any time.
                </p>
              </CheckboxGroup>
            </div>
          )
        })()}
        {/* Show QLE early submit banner conditionally */}
        {shouldShowQleEarlySubmitBanner && (
          <QleEarlySubmitBanner earliestTimeToSubmit={earliestTimeToSubmit || ''} />
        )}
        {submitError && (
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            {submitError}
          </Banner>
        )}
        {renderButtons()}
      </form>
    </div>
  )
}
